import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import ImageLightbox from '../components/ImageLightbox/ImageLightbox';
import PropertyVisual from '../components/PropertyVisual';
import { useLanguage } from '../context/LanguageContext';
import { getResidence } from '../data/residences';
import { useImageLightbox } from '../hooks/useImageLightbox';
import { getApiUrl, getPublicApiErrorMessage } from '../utils/api';
import {
  AIRPORT_TRANSFER_CURRENCY,
  AIRPORT_TRANSFER_OPTIONS,
  AIRPORT_TRANSFER_VEHICLE,
  getAirportTransferPrice,
} from '../utils/airportTransfer';
import { clearBookingSelection, readBookingSelection, saveBookingSelection } from '../utils/bookingSelection';

const MIN_NIGHTS = 3;
const MAX_NIGHTS = 60;
const MAX_GUESTS = 8;
const PHONE_INPUT_PATTERN = '[+]?[0-9]{6,20}';

const CONTACT_METHODS = [
  ['whatsapp', 'WhatsApp'],
  ['telegram', 'Telegram'],
  ['viber', 'Viber'],
  ['signal', 'Signal'],
  ['wechat', 'WeChat'],
  ['phone', 'Phone / SMS'],
  ['other', 'Other'],
];

const copyByLanguage = {
  en: {
    eyebrow: 'Booking details',
    title: 'Complete your booking',
    lead: 'Review or change the stay, recalculate the current price, then add the lead guest’s contact details.',
    missingTitle: 'This booking offer is no longer open',
    missingLead: 'Return to availability and choose the apartment and rate again.',
    back: 'Return to availability',
    guestTitle: 'Lead guest',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone number',
    nameInputRule: 'Use letters only.',
    phoneInputRule: 'Use 6–20 digits and an optional + at the beginning.',
    contactMethod: 'Preferred messenger',
    contactHandle: 'Messenger username or different number (optional)',
    contactHint: 'Use a number that works with the selected messenger whenever possible.',
    consent: 'I confirm that the stay details are correct and agree to create this booking.',
    submit: 'Confirm booking',
    submitting: 'Confirming booking…',
    genericError: 'The booking could not be confirmed. Please return to availability and try again.',
    summaryTitle: 'Your stay',
    editHint: 'You can change the dates and guest details before confirming. The selected apartment and rate stay fixed.',
    apartment: 'Apartment',
    rate: 'Rate',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    adults: 'Adults',
    children: 'Children',
    childAge: (number) => `Child ${number} age`,
    selectAge: 'Choose age',
    bookingOptions: 'Requests for your stay',
    privateParking: 'Private parking',
    babyCot: 'Baby cot available',
    highChair: 'High chair available',
    airportTransfer: 'Airport transfer',
    transferAirport: 'Arrival airport',
    selectAirport: 'Choose airport',
    tivatAirport: 'Tivat Airport',
    podgoricaAirport: 'Podgorica Airport',
    transferDestination: 'Sveti Stefan',
    flightNumber: 'Flight number',
    flightNumberPlaceholder: 'For example, JU 681',
    transferVehicle: 'Vehicle',
    accommodationPrice: 'Accommodation',
    dates: 'Dates',
    guests: 'Guests',
    ages: 'Ages',
    transferCost: 'Transfer price',
    transferPrice: 'Airport transfer',
    airportTransferError: 'Choose the arrival airport and enter the flight number for your transfer.',
    noOptions: 'No additional services selected',
    nights: 'Nights',
    total: 'Total stay',
    flexible: 'Regular rate',
    nonRefundable: 'Non-refundable rate',
    openGallery: 'Open apartment gallery',
    recalculate: 'Recalculate price',
    recalculating: 'Recalculating price…',
    priceNeedsRecalculation: 'Stay details changed — recalculate to update this price.',
    priceUpdated: 'The price and booking offer have been updated.',
    recalculateFirst: 'Recalculate the price before confirming the booking.',
    selectedRateUnavailable: 'This rate is not available for the new stay. Choose another rate and recalculate.',
    dateError: `Choose a stay between ${MIN_NIGHTS} and ${MAX_NIGHTS} nights.`,
    guestError: `A booking can include up to ${MAX_GUESTS} guests in total.`,
    childAgeError: 'Choose an age from 0 to 17 for every child.',
    requestNote: 'The reservation will be confirmed immediately. We will contact you shortly using the details provided.',
    successEyebrow: 'Booking confirmed',
    successTitle: 'Your booking is confirmed',
    successLead: 'Thank you. We will contact you shortly using the phone number or messenger you provided.',
    reference: 'Booking confirmation code',
    another: 'Search another stay',
  },
  ru: {
    eyebrow: 'Данные бронирования',
    title: 'Завершите бронирование',
    lead: 'Проверьте или измените проживание, пересчитайте актуальную стоимость, затем добавьте контакты основного гостя.',
    missingTitle: 'Это предложение больше не открыто',
    missingLead: 'Вернитесь к поиску, заново выберите апартамент и тариф.',
    back: 'Вернуться к поиску',
    guestTitle: 'Основной гость',
    firstName: 'Имя',
    lastName: 'Фамилия',
    email: 'Email',
    phone: 'Номер телефона',
    nameInputRule: 'Используйте только буквы.',
    phoneInputRule: 'Введите от 6 до 20 цифр; знак + допустим только в начале.',
    contactMethod: 'Предпочтительный мессенджер',
    contactHandle: 'Имя пользователя или другой номер (необязательно)',
    contactHint: 'По возможности укажите номер, который работает в выбранном мессенджере.',
    consent: 'Я подтверждаю данные проживания и согласен создать бронирование.',
    submit: 'Подтвердить бронирование',
    submitting: 'Подтверждаем бронирование…',
    genericError: 'Не удалось подтвердить бронирование. Вернитесь к поиску и попробуйте ещё раз.',
    summaryTitle: 'Ваше проживание',
    editHint: 'Перед подтверждением можно изменить даты и состав гостей. Выбранные апартамент и тариф остаются фиксированными.',
    apartment: 'Апартамент',
    rate: 'Тариф',
    checkIn: 'Заезд',
    checkOut: 'Выезд',
    adults: 'Взрослые',
    children: 'Дети',
    childAge: (number) => `Возраст ребёнка ${number}`,
    selectAge: 'Выберите возраст',
    bookingOptions: 'Пожелания к проживанию',
    privateParking: 'Частная парковка',
    babyCot: 'Детская кроватка',
    highChair: 'Детский стульчик',
    airportTransfer: 'Трансфер из аэропорта',
    transferAirport: 'Аэропорт прибытия',
    selectAirport: 'Выберите аэропорт',
    tivatAirport: 'Аэропорт Тиват',
    podgoricaAirport: 'Аэропорт Подгорица',
    transferDestination: 'Свети-Стефан',
    flightNumber: 'Номер рейса',
    flightNumberPlaceholder: 'Например, JU 681',
    transferVehicle: 'Автомобиль',
    accommodationPrice: 'Проживание',
    dates: 'Даты',
    guests: 'Гости',
    ages: 'Возраст',
    transferCost: 'Стоимость трансфера',
    transferPrice: 'Трансфер из аэропорта',
    airportTransferError: 'Выберите аэропорт прибытия и укажите номер рейса для трансфера.',
    noOptions: 'Дополнительные услуги не выбраны',
    nights: 'Ночей',
    total: 'За всё проживание',
    flexible: 'Обычный тариф',
    nonRefundable: 'Невозвратный тариф',
    openGallery: 'Открыть фотографии апартамента',
    recalculate: 'Пересчитать стоимость',
    recalculating: 'Пересчитываем стоимость…',
    priceNeedsRecalculation: 'Параметры изменились — пересчитайте актуальную стоимость.',
    priceUpdated: 'Стоимость и предложение бронирования обновлены.',
    recalculateFirst: 'Перед подтверждением бронирования пересчитайте стоимость.',
    selectedRateUnavailable: 'Этот тариф недоступен для новых параметров. Выберите другой тариф и пересчитайте.',
    dateError: `Выберите период от ${MIN_NIGHTS} до ${MAX_NIGHTS} ночей.`,
    guestError: `В одном бронировании может быть не более ${MAX_GUESTS} гостей.`,
    childAgeError: 'Укажите возраст от 0 до 17 лет для каждого ребёнка.',
    requestNote: 'Бронирование будет подтверждено сразу. Мы свяжемся с вами по указанным контактам.',
    successEyebrow: 'Бронирование подтверждено',
    successTitle: 'Ваше бронирование подтверждено',
    successLead: 'Спасибо. Мы свяжемся с вами по указанному номеру телефона или в выбранном мессенджере.',
    reference: 'Код подтверждения бронирования',
    another: 'Найти другое проживание',
  },
};

function BookingConfirmationPage() {
  const { language } = useLanguage();
  const copy = copyByLanguage[language];
  const [initialSelection] = useState(() => readBookingSelection());
  const [selection, setSelection] = useState(initialSelection);
  const [draft, setDraft] = useState(() => createStayDraft(initialSelection));
  const [guest, setGuest] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [contactMethod, setContactMethod] = useState('whatsapp');
  const [contactHandle, setContactHandle] = useState('');
  const [consent, setConsent] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('idle');
  const [bookingError, setBookingError] = useState('');
  const [recalculationStatus, setRecalculationStatus] = useState('idle');
  const [recalculationError, setRecalculationError] = useState('');
  const [booking, setBooking] = useState(null);

  const residence = getResidence(selection?.residenceId);
  const draftResidence = getResidence(draft?.residenceId);
  const expired = Boolean(selection?.expiresAt && Date.parse(selection.expiresAt) <= Date.now());
  const isDirty = hasStayChanged(selection, draft);
  const draftNights = getNights(draft?.checkInDateLocalized, draft?.checkOutDateLocalized);
  const draftValidationError = getDraftValidationError(draft, draftNights, copy);
  const airportTransferError = getAirportTransferValidationError(draft?.airportTransfer, copy);

  const galleryImages = useMemo(() => draftResidence?.gallery.map((src, index) => ({
    id: `${draftResidence.id}-checkout-${index + 1}`,
    src,
    alt: language === 'ru'
      ? `${draftResidence.name.ru} — фотография ${index + 1}`
      : `${draftResidence.name.en} — photo ${index + 1}`,
  })) ?? [], [draftResidence, language]);
  const lightbox = useImageLightbox(galleryImages);

  if (!selection || !residence || !draft || !draftResidence || expired) {
    return (
      <section className="page-shell booking-missing">
        <header className="page-intro page-intro_left">
          <span className="eyebrow">AMR · Sveti Stefan</span>
          <h1>{copy.missingTitle}</h1>
          <p>{copy.missingLead}</p>
          <Link className="button" to="/book#availability">{copy.back}</Link>
        </header>
      </section>
    );
  }

  const updateGuest = (field, value) => {
    const normalizedValue = field === 'firstName' || field === 'lastName'
      ? sanitizeGuestName(value)
      : field === 'phone'
        ? sanitizePhoneNumber(value)
        : value;

    setGuest((current) => ({ ...current, [field]: normalizedValue }));
    setBookingError('');
  };

  const finishGuestName = (field) => {
    setGuest((current) => ({
      ...current,
      [field]: current[field].trim().replace(/\s+/g, ' '),
    }));
  };

  const updateDraft = (changes) => {
    setDraft((current) => ({ ...current, ...changes }));
    setConsent(false);
    setRecalculationStatus('idle');
    setRecalculationError('');
    setBookingError('');
  };

  const updateChildrenCount = (childrenCount) => {
    setDraft((current) => ({
      ...current,
      childrenCount,
      childrenAges: Array.from({ length: childrenCount }, (_, index) => current.childrenAges[index] ?? ''),
      bookingOptions: childrenCount > 0
        ? current.bookingOptions
        : { ...current.bookingOptions, babyCot: false, highChair: false },
    }));
    setConsent(false);
    setRecalculationStatus('idle');
    setRecalculationError('');
    setBookingError('');
  };

  const updateChildAge = (index, age) => {
    setDraft((current) => ({
      ...current,
      childrenAges: current.childrenAges.map((currentAge, ageIndex) => (ageIndex === index ? age : currentAge)),
    }));
    setConsent(false);
    setRecalculationStatus('idle');
    setRecalculationError('');
    setBookingError('');
  };

  const updateBookingOption = (option, checked) => {
    const nextBookingOptions = { ...draft.bookingOptions, [option]: checked };
    setDraft((current) => ({ ...current, bookingOptions: nextBookingOptions }));

    // A stay change (for example, adding the first child) makes the current
    // Guesty quote stale. Keep equipment choices in the editable draft until
    // recalculation creates a quote with the new guest composition.
    if (!isDirty) {
      const nextSelection = saveBookingSelection({
        ...selection,
        bookingOptions: nextBookingOptions,
      });
      setSelection(nextSelection);
    }

    setConsent(false);
    setBookingError('');
  };

  const updateAirportTransfer = (changes) => {
    const nextAirportTransfer = { ...draft.airportTransfer, ...changes };

    if (!nextAirportTransfer.requested) {
      nextAirportTransfer.airport = '';
      nextAirportTransfer.flightNumber = '';
    }

    setDraft((current) => ({ ...current, airportTransfer: nextAirportTransfer }));

    if (!isDirty) {
      const nextSelection = saveBookingSelection({
        ...selection,
        airportTransfer: nextAirportTransfer,
      });
      setSelection(nextSelection);
    }

    setConsent(false);
    setBookingError('');
  };

  const recalculate = async () => {
    if (!isDirty || draftValidationError || recalculationStatus === 'loading') return;

    setRecalculationStatus('loading');
    setRecalculationError('');

    try {
      const guestsCount = draft.adultsCount + draft.childrenCount;
      const response = await fetch(getApiUrl('/api/guesty/quote'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          residenceId: draft.residenceId,
          checkInDateLocalized: draft.checkInDateLocalized,
          checkOutDateLocalized: draft.checkOutDateLocalized,
          adultsCount: draft.adultsCount,
          childrenCount: draft.childrenCount,
          childrenAges: draft.childrenAges.map(Number),
          guestsCount,
        }),
      });
      const data = await readResponse(response);

      if (!response.ok) throw new Error(getPublicApiErrorMessage(data.message, copy.genericError));

      const ratePlans = getQuoteRatePlans(data);
      const selectedRatePlan = ratePlans.find((ratePlan) => ratePlan.isNonRefundable === selection.isNonRefundable);

      if (!selectedRatePlan) throw new Error(copy.selectedRateUnavailable);

      const nextSelection = saveBookingSelection({
        residenceId: data.residenceId,
        quoteId: data.quoteId,
        ratePlanId: selectedRatePlan.ratePlanId,
        ratePlanName: selectedRatePlan.ratePlanName,
        isNonRefundable: selectedRatePlan.isNonRefundable,
        cancellationPolicy: selectedRatePlan.cancellationPolicy,
        ratePlans,
        currency: selectedRatePlan.currency,
        total: selectedRatePlan.total,
        nights: draftNights,
        checkInDateLocalized: draft.checkInDateLocalized,
        checkOutDateLocalized: draft.checkOutDateLocalized,
        adultsCount: draft.adultsCount,
        childrenCount: draft.childrenCount,
        childrenAges: draft.childrenAges.map(Number),
        bookingOptions: draft.bookingOptions,
        airportTransfer: draft.airportTransfer,
        guestsCount,
        expiresAt: data.expiresAt,
      });

      setSelection(nextSelection);
      setDraft(createStayDraft(nextSelection));
      setRecalculationStatus('success');
    } catch (requestError) {
      setRecalculationStatus('error');
      setRecalculationError(requestError.message || copy.genericError);
    }
  };

  const submitBooking = async (event) => {
    event.preventDefault();

    if (isDirty || draftValidationError || airportTransferError) {
      setBookingError(airportTransferError || copy.recalculateFirst);
      return;
    }

    if (!consent || bookingStatus === 'loading') return;

    setBookingStatus('loading');
    setBookingError('');

    try {
      const response = await fetch(getApiUrl('/api/guesty/reservation'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          residenceId: selection.residenceId,
          quoteId: selection.quoteId,
          ratePlanId: selection.ratePlanId,
          guest,
          contactMethod,
          contactHandle,
          adultsCount: draft.adultsCount,
          childrenCount: draft.childrenCount,
          childrenAges: draft.childrenAges.map(Number),
          bookingOptions: draft.bookingOptions,
          airportTransfer: draft.airportTransfer,
          consent,
        }),
      });
      const data = await readResponse(response);

      if (!response.ok) throw new Error(getPublicApiErrorMessage(data.message, copy.genericError));
      if (!data.reservationId || !data.status) throw new Error(copy.genericError);

      setBooking(data);
      setBookingStatus('success');
      clearBookingSelection();
    } catch (requestError) {
      setBookingStatus('error');
      setBookingError(requestError.message || copy.genericError);
    }
  };

  if (booking) {
    const reference = booking.internalReference || booking.confirmationCode || booking.reservationId;
    const bookedAirportTransfer = booking.airportTransfer || draft.airportTransfer;
    const bookedTransferPrice = getAirportTransferPrice(bookedAirportTransfer);
    const bookingOptionLabels = getBookingOptionLabels(selection.bookingOptions, copy);
    return (
      <section className="booking-success page-shell">
        <header className="booking-success__header">
          <span className="eyebrow">{copy.successEyebrow}</span>
          <h1>{copy.successTitle}</h1>
          <p className="booking-success__lead">{copy.successLead}</p>
        </header>

        <dl className="booking-success__panel">
          <div className="booking-success__row">
            <dt>{copy.apartment}</dt>
            <dd>{residence.name[language]}</dd>
          </div>
          <div className="booking-success__row">
            <dt>{copy.dates}</dt>
            <dd>{formatDateRange(selection, language)}</dd>
          </div>
          <div className="booking-success__row">
            <dt>{copy.guests}</dt>
            <dd>{formatGuestSummary(selection, copy)}</dd>
          </div>
          <div className="booking-success__row">
            <dt>{copy.accommodationPrice}</dt>
            <dd className="booking-success__value_price">{formatCurrency(selection.total, selection.currency, language)}</dd>
          </div>

          {bookedAirportTransfer?.requested && (
            <>
              <div className="booking-success__row">
                <dt>{copy.transferPrice}</dt>
                <dd className="booking-success__value_details">
                  <strong>{formatAirportTransfer(bookedAirportTransfer, copy)}</strong>
                  <span>{AIRPORT_TRANSFER_VEHICLE}</span>
                </dd>
              </div>
              <div className="booking-success__row">
                <dt>{copy.transferCost}</dt>
                <dd className="booking-success__value_price">{formatCurrency(bookedTransferPrice, AIRPORT_TRANSFER_CURRENCY, language)}</dd>
              </div>
            </>
          )}

          <div className="booking-success__row">
            <dt>{copy.reference}</dt>
            <dd className="booking-success__reference">{reference}</dd>
          </div>
          <div className="booking-success__row">
            <dt>{copy.bookingOptions}</dt>
            <dd className="booking-success__chips">
              {(bookingOptionLabels.length > 0 ? bookingOptionLabels : [copy.noOptions]).map((label) => (
                <span key={label}>{label}</span>
              ))}
            </dd>
          </div>
        </dl>
        <Link className="button booking-success__again" to="/book">{copy.another}</Link>
      </section>
    );
  }

  return (
    <section className="booking-confirmation page-shell">
      <header className="page-intro page-intro_left">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h1>{copy.title}</h1>
        <p>{copy.lead}</p>
      </header>

      <div className="booking-layout">
        <form className="booking-form" onSubmit={submitBooking}>
          <div className="booking-form__heading">
            <div>
              <span className="eyebrow">02 · Checkout</span>
              <h2>{copy.guestTitle}</h2>
            </div>
            <Link className="text-link" to="/book#availability">{copy.back}</Link>
          </div>

          <div className="guest-fields">
            <div className="field">
              <label htmlFor="guest-first-name">{copy.firstName}</label>
              <input
                id="guest-first-name"
                autoComplete="given-name"
                maxLength="80"
                title={copy.nameInputRule}
                value={guest.firstName}
                onBlur={() => finishGuestName('firstName')}
                onChange={(event) => updateGuest('firstName', event.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="guest-last-name">{copy.lastName}</label>
              <input
                id="guest-last-name"
                autoComplete="family-name"
                maxLength="80"
                title={copy.nameInputRule}
                value={guest.lastName}
                onBlur={() => finishGuestName('lastName')}
                onChange={(event) => updateGuest('lastName', event.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="guest-email">{copy.email}</label>
              <input id="guest-email" type="email" autoComplete="email" value={guest.email} onChange={(event) => updateGuest('email', event.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="guest-phone">{copy.phone}</label>
              <input
                id="guest-phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                maxLength="21"
                pattern={PHONE_INPUT_PATTERN}
                title={copy.phoneInputRule}
                value={guest.phone}
                onChange={(event) => updateGuest('phone', event.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="contact-method">{copy.contactMethod}</label>
              <select id="contact-method" value={contactMethod} onChange={(event) => setContactMethod(event.target.value)}>
                {CONTACT_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="contact-handle">{copy.contactHandle}</label>
              <input id="contact-handle" value={contactHandle} onChange={(event) => setContactHandle(event.target.value)} />
            </div>
          </div>
          <p className="booking-form__note">{copy.contactHint}</p>

          <label className="consent-field">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} required />
            <span>{copy.consent}</span>
          </label>

          {bookingError && <p className="form-error" role="alert">{bookingError}</p>}
          <button className="button booking-form__submit" type="submit" disabled={!consent || isDirty || Boolean(draftValidationError) || Boolean(airportTransferError) || bookingStatus === 'loading'}>
            {bookingStatus === 'loading' ? copy.submitting : copy.submit}
          </button>
          <p className="booking-form__note">{copy.requestNote}</p>
        </form>

        <EditableBookingSummary
          copy={copy}
          airportTransferError={airportTransferError}
          draft={draft}
          draftNights={draftNights}
          draftResidence={draftResidence}
          draftValidationError={draftValidationError}
          isDirty={isDirty}
          language={language}
          onAirportTransferChange={updateAirportTransfer}
          onChildAgeChange={updateChildAge}
          onChildrenCountChange={updateChildrenCount}
          onBookingOptionChange={updateBookingOption}
          onDraftChange={updateDraft}
          onOpenGallery={() => lightbox.open(0)}
          onRecalculate={recalculate}
          recalculationError={recalculationError}
          recalculationStatus={recalculationStatus}
          selection={selection}
        />
      </div>

      <ImageLightbox
        activeIndex={lightbox.activeIndex}
        images={galleryImages}
        isOpen={lightbox.isOpen}
        onClose={lightbox.close}
      />
    </section>
  );
}

function EditableBookingSummary({
  copy,
  airportTransferError,
  draft,
  draftNights,
  draftResidence,
  draftValidationError,
  isDirty,
  language,
  onAirportTransferChange,
  onChildAgeChange,
  onChildrenCountChange,
  onBookingOptionChange,
  onDraftChange,
  onOpenGallery,
  onRecalculate,
  recalculationError,
  recalculationStatus,
  selection,
}) {
  const today = formatInputDate(new Date());
  const earliestCheckout = draft.checkInDateLocalized ? addDays(draft.checkInDateLocalized, MIN_NIGHTS) : today;
  const transferPrice = getAirportTransferPrice(draft.airportTransfer);

  return (
    <aside className="booking-summary booking-summary_checkout">
      <button className="booking-summary__visual-button" type="button" onClick={onOpenGallery} aria-label={`${copy.openGallery}: ${draftResidence.name[language]}`}>
        <PropertyVisual src={draftResidence.image} alt={draftResidence.name[language]} className="booking-summary__visual" />
        <span>{copy.openGallery}</span>
      </button>
      <span className="eyebrow">{copy.summaryTitle}</span>
      <h2>
        <button className="booking-summary__title-button" type="button" onClick={onOpenGallery} aria-label={`${copy.openGallery}: ${draftResidence.name[language]}`}>
          {draftResidence.name[language]}
        </button>
      </h2>
      <p className="booking-summary__edit-hint">{copy.editHint}</p>

      <div className="booking-summary__row">
        <span>{copy.apartment}</span>
        <strong>{draftResidence.name[language]}</strong>
      </div>
      <div className="booking-summary__row">
        <span>{copy.rate}</span>
        <strong>{selection.isNonRefundable ? copy.nonRefundable : copy.flexible}</strong>
      </div>
      <EditRow label={copy.checkIn} htmlFor="stay-check-in">
        <input id="stay-check-in" type="date" min={today} value={draft.checkInDateLocalized} onChange={(event) => onDraftChange({ checkInDateLocalized: event.target.value })} />
      </EditRow>
      <EditRow label={copy.checkOut} htmlFor="stay-check-out">
        <input id="stay-check-out" type="date" min={earliestCheckout} value={draft.checkOutDateLocalized} onChange={(event) => onDraftChange({ checkOutDateLocalized: event.target.value })} />
      </EditRow>
      <EditRow label={copy.adults} htmlFor="stay-adults">
        <select id="stay-adults" value={draft.adultsCount} onChange={(event) => onDraftChange({ adultsCount: Number(event.target.value) })}>
          {Array.from({ length: 8 }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count}</option>)}
        </select>
      </EditRow>
      <EditRow label={copy.children} htmlFor="stay-children">
        <select id="stay-children" value={draft.childrenCount} onChange={(event) => onChildrenCountChange(Number(event.target.value))}>
          {Array.from({ length: 8 }, (_, count) => <option key={count} value={count}>{count}</option>)}
        </select>
      </EditRow>
      {draft.childrenAges.map((age, index) => (
        <EditRow label={copy.childAge(index + 1)} htmlFor={`stay-child-age-${index + 1}`} key={`stay-child-age-${index + 1}`}>
          <select id={`stay-child-age-${index + 1}`} value={age} onChange={(event) => onChildAgeChange(index, event.target.value)}>
            <option value="" disabled>{copy.selectAge}</option>
            {Array.from({ length: 18 }, (_, childAge) => <option key={childAge} value={childAge}>{childAge}</option>)}
          </select>
        </EditRow>
      ))}
      <fieldset className="booking-summary__options">
        <legend>{copy.bookingOptions}</legend>
        <BookingOptionToggle
          checked={draft.bookingOptions.privateParking}
          label={copy.privateParking}
          onChange={(checked) => onBookingOptionChange('privateParking', checked)}
        />
        {draft.childrenCount > 0 && (
          <>
            <BookingOptionToggle
              checked={draft.bookingOptions.babyCot}
              label={copy.babyCot}
              onChange={(checked) => onBookingOptionChange('babyCot', checked)}
            />
            <BookingOptionToggle
              checked={draft.bookingOptions.highChair}
              label={copy.highChair}
              onChange={(checked) => onBookingOptionChange('highChair', checked)}
            />
          </>
        )}
        <BookingOptionToggle
          checked={draft.airportTransfer.requested}
          label={copy.airportTransfer}
          onChange={(checked) => onAirportTransferChange({ requested: checked })}
        />
        {draft.airportTransfer.requested && (
          <div className="booking-summary__transfer-fields">
            <EditRow label={copy.transferAirport} htmlFor="airport-transfer-airport">
              <select
                id="airport-transfer-airport"
                value={draft.airportTransfer.airport}
                onChange={(event) => onAirportTransferChange({ airport: event.target.value })}
              >
                <option value="">{copy.selectAirport}</option>
                <option value="tivat">{copy.tivatAirport} → {copy.transferDestination} — €{AIRPORT_TRANSFER_OPTIONS.tivat.price}</option>
                <option value="podgorica">{copy.podgoricaAirport} → {copy.transferDestination} — €{AIRPORT_TRANSFER_OPTIONS.podgorica.price}</option>
              </select>
            </EditRow>
            <EditRow label={copy.flightNumber} htmlFor="airport-transfer-flight-number">
              <input
                id="airport-transfer-flight-number"
                maxLength="40"
                placeholder={copy.flightNumberPlaceholder}
                value={draft.airportTransfer.flightNumber}
                onChange={(event) => onAirportTransferChange({ flightNumber: event.target.value })}
              />
            </EditRow>
            <div className="booking-summary__transfer-vehicle">
              <span>{copy.transferVehicle}</span>
              <strong>{AIRPORT_TRANSFER_VEHICLE}</strong>
            </div>
          </div>
        )}
      </fieldset>
      {airportTransferError && <p className="form-error booking-summary__error" role="alert">{airportTransferError}</p>}
      <div className="booking-summary__row">
        <span>{copy.nights}</span>
        <strong>{draftNights > 0 ? draftNights : '—'}</strong>
      </div>

      {draft.airportTransfer.requested && (
        <div className="booking-summary__price-breakdown">
          <div>
            <span>{copy.accommodationPrice}</span>
            <strong>{formatCurrency(selection.total, selection.currency, language)}</strong>
          </div>
          <div>
            <span>{copy.transferPrice}</span>
            <strong>{transferPrice > 0 ? formatCurrency(transferPrice, AIRPORT_TRANSFER_CURRENCY, language) : '—'}</strong>
          </div>
        </div>
      )}

      <div className={`booking-summary__total ${isDirty ? 'booking-summary__total_stale' : ''}`}>
        <span>{copy.total}</span>
        <strong>{formatCurrency(selection.total, selection.currency, language)}</strong>
        {isDirty && <small>{copy.priceNeedsRecalculation}</small>}
      </div>

      {draftValidationError && <p className="form-error booking-summary__error" role="alert">{draftValidationError}</p>}
      {recalculationError && <p className="form-error booking-summary__error" role="alert">{recalculationError}</p>}
      {recalculationStatus === 'success' && <p className="booking-message booking-summary__message">{copy.priceUpdated}</p>}
      {isDirty && (
        <button className="button booking-summary__recalculate" type="button" onClick={onRecalculate} disabled={Boolean(draftValidationError) || recalculationStatus === 'loading'}>
          {recalculationStatus === 'loading' ? copy.recalculating : copy.recalculate}
        </button>
      )}
    </aside>
  );
}

function EditRow({ children, htmlFor, label }) {
  return (
    <div className="booking-summary__edit-row">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

function BookingOptionToggle({ checked, label, onChange }) {
  return (
    <label className="booking-summary__option">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}

function createStayDraft(selection) {
  if (!selection) return null;
  return {
    residenceId: selection.residenceId,
    isNonRefundable: selection.isNonRefundable,
    checkInDateLocalized: selection.checkInDateLocalized,
    checkOutDateLocalized: selection.checkOutDateLocalized,
    adultsCount: selection.adultsCount,
    childrenCount: selection.childrenCount,
    childrenAges: [...selection.childrenAges],
    bookingOptions: { ...selection.bookingOptions },
    airportTransfer: { ...selection.airportTransfer },
  };
}

function hasStayChanged(selection, draft) {
  if (!selection || !draft) return false;
  return selection.checkInDateLocalized !== draft.checkInDateLocalized
    || selection.checkOutDateLocalized !== draft.checkOutDateLocalized
    || selection.adultsCount !== draft.adultsCount
    || selection.childrenCount !== draft.childrenCount
    || selection.childrenAges.join(',') !== draft.childrenAges.join(',');
}

function getDraftValidationError(draft, nights, copy) {
  if (!draft) return copy.genericError;
  if (!draft.checkInDateLocalized || !draft.checkOutDateLocalized || nights < MIN_NIGHTS || nights > MAX_NIGHTS) return copy.dateError;

  const guestsCount = draft.adultsCount + draft.childrenCount;
  if (guestsCount < 1 || guestsCount > MAX_GUESTS) return copy.guestError;
  if (draft.childrenAges.length !== draft.childrenCount || draft.childrenAges.some((age) => age === '' || Number(age) < 0 || Number(age) > 17)) return copy.childAgeError;
  return '';
}

function getAirportTransferValidationError(airportTransfer, copy) {
  if (airportTransfer?.requested !== true) return '';
  if (!AIRPORT_TRANSFER_OPTIONS[airportTransfer.airport] || !airportTransfer.flightNumber?.trim()) {
    return copy.airportTransferError;
  }
  return '';
}

function getQuoteRatePlans(quote) {
  const ratePlans = Array.isArray(quote?.ratePlans) ? quote.ratePlans : [];
  if (ratePlans.length > 0) return ratePlans;

  if (!quote?.ratePlanId) return [];
  return [{
    ratePlanId: quote.ratePlanId,
    ratePlanName: quote.ratePlanName,
    isNonRefundable: false,
    cancellationPolicy: [],
    cancellationFee: null,
    currency: quote.currency,
    total: quote.total,
  }];
}

function getNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  return Math.max(0, Math.round((Date.parse(checkOut) - Date.parse(checkIn)) / 86_400_000));
}

function formatDateRange(selection, language) {
  return `${formatDisplayDate(selection.checkInDateLocalized, language)} — ${formatDisplayDate(selection.checkOutDateLocalized, language)}`;
}

function formatDisplayDate(dateString, language) {
  return new Date(`${dateString}T12:00:00`).toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatCurrency(value, currency = 'EUR', language = 'en') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(language === 'ru' ? 'ru-RU' : 'en-GB', { style: 'currency', currency, maximumFractionDigits: Number.isInteger(amount) ? 0 : 2 }).format(amount);
}

function getBookingOptionLabels(options, copy) {
  return [
    options?.privateParking && copy.privateParking,
    options?.babyCot && copy.babyCot,
    options?.highChair && copy.highChair,
  ].filter(Boolean);
}

function formatGuestSummary(selection, copy) {
  const childAges = selection.childrenCount > 0 ? ` · ${copy.ages}: ${selection.childrenAges.join(', ')}` : '';
  return `${copy.adults}: ${selection.adultsCount} · ${copy.children}: ${selection.childrenCount}${childAges}`;
}

function formatAirportTransfer(airportTransfer, copy) {
  const airport = airportTransfer.airport === 'tivat' ? copy.tivatAirport : copy.podgoricaAirport;
  return `${airport} → ${copy.transferDestination} · ${copy.flightNumber}: ${airportTransfer.flightNumber}`;
}

function formatInputDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sanitizeGuestName(value) {
  return value.replace(/[^\p{L}\p{M}\s]/gu, '').replace(/\s{2,}/g, ' ');
}

function sanitizePhoneNumber(value) {
  const digits = value.replace(/\D/g, '').slice(0, 20);
  return value.trimStart().startsWith('+') ? `+${digits}` : digits;
}

function addDays(dateString, days) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatInputDate(date);
}

async function readResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export default BookingConfirmationPage;
